import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "@/components/Header";
import PerformersSection from "@/components/PerformersSection";
import { HeroImage } from "@/components/HeroImage";
import { usePageSEO } from "@/hooks/usePageTitle";
import { useProductionTeam } from "@/hooks/useProductionTeam";
import { useHeroImageSettings } from "@/hooks/useHeroImageSettings";
import GlobalLoader from "@/components/GlobalLoader";
import { supabase } from "@/integrations/supabase/client";
import { convertMarkdownToHtml } from "@/utils/markdownHelpers";

const About = () => {
  const { data: productionTeam, isLoading, error } = useProductionTeam();
  const { isLoading: heroImageLoading } = useHeroImageSettings("about");

  const { data: introPage, isLoading: introLoading } = useQuery({
    queryKey: ["site-page", "om-oss-intro"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("site_pages")
        .select("content")
        .eq("slug", "om-oss-intro")
        .single();
      if (error) throw error;
      return data;
    },
  });

  // Koordinerad loading state
  const isPageLoading = isLoading || heroImageLoading || introLoading;

  usePageSEO({
    title: "Om oss | Lilla improteatern",
    description:
      "Lär känna teamet bakom Lilla improteatern. Hjalmar, Ellen och David – erfarna improvisatörer som driver Stockholms nya improv comedy-scen.",
    canonicalPath: "/om-oss",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "Om Lilla improteatern",
      description: "Information om teamet och verksamheten bakom Lilla improteatern",
      mainEntity: {
        "@type": "Organization",
        name: "Lilla improteatern",
        founder: [
          { "@type": "Person", name: "Hjalmar Hardestam" },
          { "@type": "Person", name: "Ellen Bobeck" },
          { "@type": "Person", name: "David Rosenqvist" },
        ],
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show skeleton while loading
  if (isPageLoading) {
    return <GlobalLoader />;
  }

  const introHtml = convertMarkdownToHtml(introPage?.content || "");

  return (
    <>
      <Header />

      <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
        {/* Hero Image */}
        <HeroImage pageKey="about" priority={true} />

        <div className="relative z-10 mx-0 md:mx-auto max-w-[800px] -mt-16">
          <div className="bg-[#F3F3F3] rounded-t-lg overflow-hidden">
            <div className="p-6 md:p-8 space-y-8">
              {/* Om oss-info (redigerbar) */}
              <div
                className="space-y-4"
                dangerouslySetInnerHTML={{ __html: introHtml }}
              />

              {/* Personerna bakom */}
              {productionTeam && productionTeam.length > 0 ? (
                <PerformersSection performers={productionTeam} title="Produktionsteam" />
              ) : (
                <div className="mb-6">
                  <h2 className="mb-3">Produktionsteam</h2>
                  <div className="bg-theatre-light/10 rounded-none border-[rgb(var(--course-outline-red))] border-3 p-4">
                    <p className="text-center text-muted-foreground">Inga produktionsteamedlemmar har lagts till än.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
